"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GridTableApiImpl = exports.useGridTableApi = void 0;
const mobx_1 = require("mobx");
const mobx_utils_1 = require("mobx-utils");
const react_1 = require("react");
const index_1 = require("../index");
const TableState_1 = require("./utils/TableState");
/**
 * Creates an `api` handle to drive a `GridTable`.
 *
 * ```
 * const api = useGridTableApi<Row>();
 * const count = useComputed(() => api.getSelectedRows().length, [api]);
 * ...
 * return <GridTable api={api} />
 * ```
 *
 * This is very similar to a `useRef`, except that the parent function has
 * immediate access to `api` and can use it for `useComputed`, instead of
 * having to wait for `ref.current` to be set after the child `GridTable`
 * has run.
 */
function useGridTableApi() {
    return (0, react_1.useMemo)(() => new GridTableApiImpl(), []);
}
exports.useGridTableApi = useGridTableApi;
// Using `FooImpl`to keep the public GridTableApi definition separate.
class GridTableApiImpl {
    constructor() {
        // This is public to GridTable but not exported outside of Beam
        this.tableState = new TableState_1.TableState(this);
        this.virtuosoRef = { current: null };
        // This instance gets spread into each row's GridRowApi, so bind the methods up-front
        bindMethods(this);
        // Memoize these so that if the user is creating new `data` instances on every render, they
        // can use `getSelectedRowIds` to observer a stable list of `[pi:1, pi:2]`, etc.
        this.getVisibleRowsImpl = (0, mobx_utils_1.computedFn)(this.getVisibleRowsImpl, { equals: mobx_1.comparer.shallow });
        this.getVisibleRowIdsImpl = (0, mobx_utils_1.computedFn)(this.getVisibleRowIdsImpl, { equals: mobx_1.comparer.shallow });
        this.getSelectedRowsImpl = (0, mobx_utils_1.computedFn)(this.getSelectedRowsImpl, { equals: mobx_1.comparer.shallow });
        this.getSelectedRowIdsImpl = (0, mobx_utils_1.computedFn)(this.getSelectedRowIdsImpl, { equals: mobx_1.comparer.shallow });
    }
    /** Called once by the GridTable when it takes ownership of this api instance. */
    init(persistCollapse, virtuosoRef) {
        // Technically this drives both row-collapse and column-expanded
        if (persistCollapse)
            this.tableState.loadCollapse(persistCollapse);
        this.virtuosoRef = virtuosoRef;
        this.lookup = (0, index_1.createRowLookup)(this, virtuosoRef);
    }
    scrollToIndex(index) {
        this.virtuosoRef.current && this.virtuosoRef.current.scrollToIndex(index);
    }
    getSelectedRowIds(kind) {
        return this.getSelectedRowIdsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getSelectedRowIdsImpl(kind) {
        return this.tableState.selectedRows.filter((rs) => !kind || rs.kind === kind).map((rs) => rs.row.id);
    }
    getSelectedRows(kind) {
        return this.getSelectedRowsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getSelectedRowsImpl(kind) {
        return this.tableState.selectedRows.filter((rs) => !kind || rs.kind === kind).map((rs) => rs.row);
    }
    // The `any` is not great, but getting the overload to handle the optional kind is annoying
    getVisibleRows(kind) {
        return this.getVisibleRowsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getVisibleRowsImpl(kind) {
        return this.tableState.visibleRows.filter((row) => !kind || row.kind === kind).map((rs) => rs.row);
    }
    getVisibleRowIds(kind) {
        return this.getVisibleRowIdsImpl(kind !== null && kind !== void 0 ? kind : undefined);
    }
    // impl with required param for computedFn
    getVisibleRowIdsImpl(kind) {
        return this.tableState.visibleRows.filter((row) => !kind || row.kind === kind).map((rs) => rs.row.id);
    }
    clearSelections(id) {
        this.tableState.selectRow("header", false);
    }
    setActiveRowId(id) {
        this.tableState.activeRowId = id;
    }
    setActiveCellId(id) {
        this.tableState.activeCellId = id;
    }
    selectRow(id, selected = true) {
        this.tableState.selectRow(id, selected);
    }
    toggleCollapsedRow(id) {
        this.tableState.toggleCollapsed(id);
    }
    isCollapsedRow(id) {
        return this.tableState.isCollapsed(id);
    }
    setVisibleColumns(ids) {
        this.tableState.setVisibleColumns(ids);
    }
    getVisibleColumnIds() {
        return this.tableState.visibleColumnIds;
    }
    deleteRows(ids) {
        this.tableState.deleteRows(ids);
    }
}
exports.GridTableApiImpl = GridTableApiImpl;
function bindMethods(instance) {
    Object.getOwnPropertyNames(Object.getPrototypeOf(instance)).forEach((key) => {
        if (instance[key] instanceof Function && key !== "constructor")
            instance[key] = instance[key].bind(instance);
    });
}
